<template>
    <div class="reviews-container">
        <div class="reviews-header">
            <h2>Customer Reviews</h2>
            <div v-if="overallRating" class="overall-rating-wrap">
                <div class="overall-rating">
                    <span class="rating-number">{{ overallRating.toFixed(1) }}</span>
                    <span class="stars">{{ getStars(Math.round(overallRating)) }}</span>
                </div>
                <div class="total-reviews">Based on {{ totalReviews }} reviews</div>
            </div>
        </div>

        <div v-if="loading" class="loading">Loading reviews...</div>

        <div v-else-if="error" class="error">
            {{ error }}
            <div v-if="errorDetails" style="font-size: 0.85em; margin-top: 10px; color: #666;">
                {{ errorDetails }}
            </div>
        </div>

        <div v-else-if="filteredReviews.length > 0" class="reviews-grid">
            <div v-for="review in filteredReviews" :key="review.name" class="review-card">
                <div class="review-header">
                    <img
                        :src="getPhotoUrl(review)"
                        :alt="getAuthorName(review)"
                        class="reviewer-photo"
                        @error="handleImageError"
                    >
                    <div class="reviewer-info">
                        <div class="reviewer-name">{{ getAuthorName(review) }}</div>
                        <div class="review-date">{{ getPublishTime(review) }}</div>
                    </div>
                </div>
                <div class="review-rating">{{ getStars(getRating(review)) }}</div>
                <div class="review-text">{{ getReviewText(review) }}</div>
            </div>
        </div>

        <div v-else class="loading">No reviews available.</div>

        <div class="google-link">
            <a href="https://search.google.com/local/writereview?placeid=ChIJPbKWYoh3flMR8x7e6sYr9Mo"
               target="_blank"
               rel="noopener">
                Write a Review on Google
            </a>
        </div>
    </div>
</template>

<script>
export default {
    name: 'GoogleReviews',

    data() {
        return {
            placeId: 'ChIJPbKWYoh3flMR8x7e6sYr9Mo',
            cacheKey: 'google_reviews_cache',
            cacheDuration: 24 * 60 * 60 * 1000,

            loading: true,
            error: null,
            errorDetails: null,
            overallRating: null,
            totalReviews: 0,
            reviews: [],
            cacheTimestamp: null
        };
    },

    computed: {
        filteredReviews() {
            if (!this.reviews || !Array.isArray(this.reviews)) {
                return [];
            }
            return this.reviews.filter(review => {
                const rating = this.getRating(review);
                return rating === 4 || rating === 5;
            });
        },

        hoursAgo() {
            if (!this.cacheTimestamp) return 0;
            const now = new Date();
            const cacheDate = new Date(this.cacheTimestamp);
            return Math.floor((now - cacheDate) / (1000 * 60 * 60));
        },

        nextUpdateTime() {
            if (!this.cacheTimestamp) return '';
            const nextUpdate = new Date(this.cacheTimestamp + this.cacheDuration);
            return nextUpdate.toLocaleString();
        }
    },

    mounted() {
        this.fetchReviews();
    },

    methods: {
        getStars(rating) {
            const fullStar = '★';
            const emptyStar = '☆';
            const validRating = Math.max(0, Math.min(5, rating || 0));
            return fullStar.repeat(validRating) + emptyStar.repeat(5 - validRating);
        },

        getRating(review) {
            return review && typeof review.rating === 'number' ? review.rating : 5;
        },

        getPhotoUrl(review) {
            if (review && review.authorAttribution && review.authorAttribution.photoUri) {
                return review.authorAttribution.photoUri;
            }
            return 'https://via.placeholder.com/40';
        },

        getAuthorName(review) {
            if (review && review.authorAttribution && review.authorAttribution.displayName) {
                return review.authorAttribution.displayName;
            }
            return 'Anonymous';
        },

        getPublishTime(review) {
            if (review && review.relativePublishTimeDescription) {
                return review.relativePublishTimeDescription;
            }
            return 'Recently';
        },

        getReviewText(review) {
            if (review && review.text && review.text.text) {
                return review.text.text;
            }
            return 'No review text provided.';
        },

        handleImageError(event) {
            if (event && event.target) {
                event.target.src = 'https://via.placeholder.com/40';
            }
        },

        getCachedData() {
            try {
                const cached = localStorage.getItem(this.cacheKey);
                if (!cached) return null;

                const parsed = JSON.parse(cached);
                if (!parsed || !parsed.data || !parsed.timestamp) {
                    return null;
                }

                const { data, timestamp } = parsed;
                const now = new Date().getTime();

                if (now - timestamp < this.cacheDuration) {
                    return { data, timestamp };
                }

                localStorage.removeItem(this.cacheKey);
                return null;
            } catch (err) {
                console.error('Error reading cache:', err);
                return null;
            }
        },

        setCachedData(data) {
            try {
                const cacheObject = {
                    data: data,
                    timestamp: new Date().getTime()
                };
                localStorage.setItem(this.cacheKey, JSON.stringify(cacheObject));
            } catch (err) {
                console.error('Error setting cache:', err);
            }
        },

        async fetchReviews() {
            try {
                const cached = this.getCachedData();
                if (cached) {
                    console.log('Using cached reviews');
                    this.displayReviews(cached.data);
                    this.cacheTimestamp = cached.timestamp;
                    this.loading = false;
                    return;
                }

                console.log('Fetching fresh reviews from API');

                const response = await fetch('/api/reviews.php');

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('API Response Error:', response.status, errorData);

                    this.error = 'Unable to load reviews from Google.';
                    this.errorDetails = `Status: ${response.status}`;
                    this.loading = false;
                    return;
                }

                const data = await response.json();

                console.log('Reviews data received:', data);

                this.setCachedData(data);
                this.cacheTimestamp = new Date().getTime();

                this.displayReviews(data);
                this.loading = false;

            } catch (err) {
                console.error('Error fetching reviews:', err);
                this.error = 'Unable to load reviews. Please try again later.';
                this.errorDetails = err.message || 'Unknown error';
                this.loading = false;
            }
        },

        displayReviews(data) {
            if (!data) {
                this.reviews = [];
                return;
            }

            this.overallRating = data.rating || null;
            this.totalReviews = data.userRatingCount || 0;
            this.reviews = Array.isArray(data.reviews) ? data.reviews : [];
        }
    }
};
</script>


// WEBPACK FOOTER //
// html/js/src/common/google_reviews.vue